import { createSlice } from "@reduxjs/toolkit";
import Enums from "../../enums";
import { IAccountOpeningFeeForm } from "../../Pages/ProductSelection/ProductSelectionForm";
import api from "../../Services/ApiServices";
import { IAORProductRelatedDetail } from "../../Types/AccountOpeningRequest";
import AuthInfoContext from "../../Types/AuthInfoContext";
import { IOTPSession } from "../../Types/OTPSession";
import { dispatch, RootState } from "../store";
import { setAuthInfo, setStage } from "./common";

interface IStep2State {
  isLoading: boolean;
  error?: any;
  objAORProductDetail?: IAORProductRelatedDetail;

  setAORDetailLoading: boolean;
  setAORDetailError?: any;
  setAORDetailResponse?: IOTPSession;
}

const initialState: IStep2State = {
  isLoading: false,
  setAORDetailLoading: false,
};

const slice = createSlice({
  name: "stage2",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = undefined;
    },
    AORProductDetailSuccess(state, action) {
      state.objAORProductDetail = action.payload;
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    //submit
    startSetAORDetailLoading(state) {
      state.setAORDetailLoading = true;
    },
    setAORDetailSuccess(state, action) {
      state.setAORDetailResponse = action.payload;
      state.setAORDetailLoading = false;
    },
    setAORDetailHasError(state, action) {
      state.setAORDetailLoading = false;
      state.setAORDetailError = action.payload;
    },
    resetFields() {
      return { ...initialState };
    },
  },
});

// Reducer
export default slice.reducer;

export const { resetFields } = slice.actions;

export const getLimitedAORDetails = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.startLoading());

      const { objAuthInfoResponse } = getState().common;

      let response = await api.GetAORDetails({
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });
      if (!response.Status) throw new Error(response.Message);

      await dispatch(slice.actions.AORProductDetailSuccess(response));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.hasError(ex));
    }
  };
};

export const setAORProduct = (formValues: IAccountOpeningFeeForm) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      let SPICBWs = 0,
        SSICBWs = 0;
      if (formValues.productEquity)
        SPICBWs += Enums.ACCOUNT_OPENING_PRODUCT_BW.EQUITY;
      if (formValues.trading)
        SPICBWs += Enums.ACCOUNT_OPENING_PRODUCT_BW.TRADING;
      if (formValues.newDemat)
        SPICBWs += Enums.ACCOUNT_OPENING_PRODUCT_BW.DEMAT;

      if (formValues.equity) SSICBWs += Enums.SEGMENT_INTERNAL_CODE_BW.EQUITY;
      if (formValues.derivative)
        SSICBWs += Enums.SEGMENT_INTERNAL_CODE_BW.DERIVATIVE;
      if (formValues.currency)
        SSICBWs += Enums.SEGMENT_INTERNAL_CODE_BW.CURRENCY;
      if (formValues.commodity)
        SSICBWs += Enums.SEGMENT_INTERNAL_CODE_BW.COMMODITY;
      if (formValues.slbSegment) SSICBWs += Enums.SEGMENT_INTERNAL_CODE_BW.SLB;

      // let IDDPICT = "0";
      // if (
      //   formValues.IsDDPIConsentForBOTransferForTradeSettlementTaken === "1" ||
      //   formValues.IsDDPIConsentForPledgeForMarginRequirementTaken === "1" ||
      //   formValues.IsDDPIConsentForMutualFundExchangeOrderTaken === "1" ||
      //   formValues.IsDDPIConsentForOpenOfferShareTenderTaken === "1"
      // ) {
      //   IDDPICT = "1";
      // }

      // DJT++ 25062025 [Start]
      let IDDPICFBOTFTST = "0";
      if (formValues.IsDDPIConsentForBOTransferForTradeSettlementTaken)
        IDDPICFBOTFTST =
          formValues.IsDDPIConsentForBOTransferForTradeSettlementTaken;

      let IDDPICFPFMRT = "0";
      if (formValues.IsDDPIConsentForPledgeForMarginRequirementTaken)
        IDDPICFPFMRT =
          formValues.IsDDPIConsentForPledgeForMarginRequirementTaken;

      let IDDPICFMFEOT = "0";
      if (formValues.IsDDPIConsentForMutualFundExchangeOrderTaken)
        IDDPICFMFEOT = formValues.IsDDPIConsentForMutualFundExchangeOrderTaken;

      let IDDPICFOFSTT = "0";
      if (formValues.IsDDPIConsentForOpenOfferShareTenderTaken)
        IDDPICFOFSTT = formValues.IsDDPIConsentForOpenOfferShareTenderTaken;

      let ISMSAOI = "0";
      if (formValues.DematAc_IsSMSAlertOptedIn)
        ISMSAOI = formValues.DematAc_IsSMSAlertOptedIn;

      let IEOI = "0";
      if (formValues.DematAc_IsEasiOptedIn)
        IEOI = formValues.DematAc_IsEasiOptedIn;
      // DJT++ 25062025 [End]

      await dispatch(slice.actions.startSetAORDetailLoading());
      const { objAuthInfoResponse } = getState().common;
      // const { objAORProductDetail } = getState().stage2;

      let response = await api.setAORProduct({
        AOSAI: JSON.stringify(objAuthInfoResponse),
        SPICBWs,
        SSICBWs,
        // DJT-- 26062025 IDDPICT,
        IDDPICFBOTFTST, // DJT++ 25062025
        IDDPICFPFMRT, // DJT++ 25062025
        IDDPICFMFEOT, // DJT++ 25062025
        IDDPICFOFSTT, // DJT++ 25062025
        ISMSAOI, // DJT++ 25062025
        IEOI, // DJT++ 25062025
      });
      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );
      await dispatch(slice.actions.setAORDetailSuccess(response));
      if (!objAuthContext.Status)
        throw new Error(objAuthContext.Message || "Unexpected error.");
      await dispatch(setAuthInfo(response));
      if (objAuthContext.NextStage > 0) {
        await dispatch(setStage(objAuthContext.NextStage));
      } else {
        throw new Error("Unexpected stage identified.");
      }
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.setAORDetailHasError(ex));
    }
  };
};
